import { router } from "@inertiajs/react";
import { BanknoteIcon, ExternalLinkIcon, LinkIcon } from "lucide-react";
import { type ReactNode, useState } from "react";
import { useTranslation } from "react-i18next";
import { CopyButton } from "@/components/shared/copy-button";
import {
	AlertDialog,
	AlertDialogAction,
	AlertDialogCancel,
	AlertDialogContent,
	AlertDialogDescription,
	AlertDialogFooter,
	AlertDialogHeader,
	AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Button, buttonVariants } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Textarea } from "@/components/ui/textarea";
import {
	COMPANY_BANK_ACCOUNTS,
	destinationFromAccount,
	OTHER_BANK_ACCOUNT,
	PAYMENT_METHOD,
	prefillFromAccount,
} from "@/lib/orders/manual-payment";
import { cn, formatCurrency } from "@/lib/utils";

/* Hallmark · component: settle-payment actions · genre: modern-minimal · theme: project tokens
 * states: default · hover · focus-visible · active · disabled · loading · error · success
 * contrast: pass — one filled primary per row, underlined muted for the exception
 */

// Mirrors `serialize_payment` in PreparationOrdersAppointmentService. Read straight
// from the database the Go backend writes to, so a package change shows up here
// without an extra API round trip.
export interface OrderPayment {
	id: string;
	paymentType: string;
	status: string;
	amount: number;
	paymentMethod: string | null;
	paymentGateway: string | null;
	bankCode: string | null;
	paymentDestination: string | null;
	invoiceUrl: string | null;
	description: string | null;
	expiresAt: string | null;
	paidAt: string | null;
	refundedAt: string | null;
	createdAt: string;
	settleable: boolean;
	/** EXPIRED, or PENDING past `expiresAt` before the webhook marked it. */
	linkExpired: boolean;
}

export const isRefund = (payment: OrderPayment) =>
	payment.paymentType === "ADJUSTMENT_REFUND";

// Four different jobs wear the same amber tag, and only this tells them apart:
// send the link, replace a dead one, wait for a link already sent, or move the money out.
const waitingOn = (payment: OrderPayment) => {
	if (isRefund(payment)) return "refund";
	if (payment.linkExpired) return "expired";
	return payment.invoiceUrl ? "payment" : "invoice";
};

export const hasLiveLink = (payment: OrderPayment) =>
	!isRefund(payment) && !!payment.invoiceUrl && !payment.linkExpired;

// A charge is billable online whenever it has no live link — the endpoint targets any
// row by its ID, so a re-billed PAYMENT and an ADJUSTMENT_CHARGE take the same path.
export const canGenerateInvoice = (payment: OrderPayment) =>
	payment.settleable && !isRefund(payment) && !hasLiveLink(payment);

// The order-level RegenerateInvoiceForm offers the same first five; a single charge
// may also need a full day.
export const INVOICE_EXPIRY_OPTIONS = [
	{ value: "30", key: "minutes_30" },
	{ value: "60", key: "hour_1" },
	{ value: "180", key: "hours_3" },
	{ value: "300", key: "hours_5" },
	{ value: "720", key: "hours_12" },
] as const;
const PAYMENT_EXPIRY_OPTIONS = [
	...INVOICE_EXPIRY_OPTIONS,
	{ value: "1440", key: "hours_24" },
] as const;

/**
 * The three ways an admin closes one open payment row: issue the link, mark the
 * money received, or mark the refund transferred.
 *
 * Lives on its own so the change-package dialog can offer the same three without
 * sending anyone to the order-details panel to look for them.
 */
export function SettlePaymentActions({
	orderId,
	payment,
	onDone,
	showInvoice = false,
}: {
	orderId: string;
	payment: OrderPayment;
	onDone: () => void;
	/** Off in the ledger, where the row already carries its own link line. */
	showInvoice?: boolean;
}) {
	const { t } = useTranslation("appointments", { useSuspense: false });
	const [activeForm, setActiveForm] = useState<
		"paid" | "refunded" | "invoice" | null
	>(null);
	// Which exception is being confirmed, if any.
	const [confirming, setConfirming] = useState<"paid" | "invoice" | null>(null);
	// A charge the patient can still pay online. The link is the answer here; the
	// manual settle behind it is a fallback, not a second route of equal standing.
	const liveLink = hasLiveLink(payment);
	// A dead link is the row's whole problem, so a new one is the expected ending —
	// not an exception to confirm. The backend only marks a PENDING row paid.
	const needsNewLink = !isRefund(payment) && payment.linkExpired;

	const done = () => {
		setActiveForm(null);
		onDone();
	};

	return (
		<>
			{/* The link itself, before anything that can be done with it. It is the
			    payable thing on this row, and an admin's first move is usually to send
			    it on — so it is a field you can read and copy, not a word hidden inside
			    an anchor. Read-only: the URL comes from the gateway. */}
			{showInvoice && liveLink && payment.invoiceUrl && (
				<div className="relative mt-2">
					<Input
						readOnly
						value={payment.invoiceUrl}
						aria-label={t("payments.actions.invoice_url_label")}
						onFocus={(event) => event.currentTarget.select()}
						className="h-9 pe-9 font-mono text-xs shadow-inner bg-sidebar"
					/>
					<CopyButton
						value={payment.invoiceUrl}
						label={t("payments.actions.copy_invoice_url")}
						className="absolute inset-y-0 end-0 flex h-full w-9 items-center justify-center rounded-e-md"
					/>
				</div>
			)}

			{/* One action, because the row only has one expected ending. Offering both
			    settle routes as buttons made every row argue with the choice that
			    created it: a charge billed by hand asking to be put online, a charge
			    already online asking to be settled by hand. The other route is still
			    here — one line down, phrased as the exception it is. */}
			<div className="mt-2 flex flex-wrap items-center gap-x-3 gap-y-2">
				{needsNewLink ? (
					// The form below carries its own submit with the same label.
					activeForm !== "invoice" && (
						<Button
							type="button"
							size="sm"
							onClick={() => setActiveForm("invoice")}
						>
							<LinkIcon className="size-3.5" />
							{t("payments.actions.new_link")}
						</Button>
					)
				) : liveLink ? (
					// In the ledger the row already carries its own link line, so there is
					// no primary to repeat — only the exception below.
					showInvoice && (
						<a
							href={payment.invoiceUrl ?? ""}
							target="_blank"
							rel="noreferrer"
							className={cn(buttonVariants({ size: "sm" }))}
						>
							<ExternalLinkIcon className="size-3.5" />
							{t("payments.actions.open_invoice")}
						</a>
					)
				) : (
					<Button
						type="button"
						size="sm"
						onClick={() =>
							setActiveForm(isRefund(payment) ? "refunded" : "paid")
						}
					>
						<BanknoteIcon className="size-3.5" />
						{isRefund(payment)
							? t("payments.actions.mark_refunded")
							: t("payments.actions.mark_paid")}
					</Button>
				)}

				{/* Named for the situation, not the mechanism: an admin reaches for it
				    when something did not go the way the preset intended, so the label
				    has to describe that something. */}
				{liveLink || (needsNewLink && payment.status === "PENDING") ? (
					<ExceptionAction onClick={() => setConfirming("paid")}>
						{t("payments.actions.mark_paid_transfer")}
					</ExceptionAction>
				) : (
					!needsNewLink &&
					canGenerateInvoice(payment) && (
						<ExceptionAction onClick={() => setConfirming("invoice")}>
							{t("payments.actions.generate_invoice_instead")}
						</ExceptionAction>
					)
				)}
			</div>

			{/* The exception asks once before it is taken. Not because it is dangerous —
			    neither branch destroys anything — but because it undoes a decision made
			    minutes ago, and the likeliest reason to be here is having forgotten what
			    that decision already set in motion. */}
			<AlertDialog
				open={!!confirming}
				onOpenChange={(open) => !open && setConfirming(null)}
			>
				{confirming && (
					<AlertDialogContent>
						<AlertDialogHeader>
							<AlertDialogTitle>
								{t(`payments.confirm.${confirming}.title`)}
							</AlertDialogTitle>
							<AlertDialogDescription>
								{t(`payments.confirm.${confirming}.description`)}
							</AlertDialogDescription>
						</AlertDialogHeader>
						<AlertDialogFooter>
							<AlertDialogCancel>
								{t("payments.actions.cancel")}
							</AlertDialogCancel>
							<AlertDialogAction
								onClick={() => {
									const taking = confirming;
									setConfirming(null);
									setActiveForm(taking);
								}}
							>
								{t(`payments.confirm.${confirming}.submit`)}
							</AlertDialogAction>
						</AlertDialogFooter>
					</AlertDialogContent>
				)}
			</AlertDialog>

			{activeForm === "paid" && (
				<MarkPaidForm
					orderId={orderId}
					paymentId={payment.id}
					onDone={done}
					onCancel={() => setActiveForm(null)}
				/>
			)}
			{activeForm === "invoice" && (
				<GenerateLinkForm
					orderId={orderId}
					payment={payment}
					onDone={done}
					onCancel={() => setActiveForm(null)}
				/>
			)}
			{activeForm === "refunded" && (
				<MarkRefundedForm
					orderId={orderId}
					paymentId={payment.id}
					onDone={done}
					onCancel={() => setActiveForm(null)}
				/>
			)}
		</>
	);
}

/**
 * The way out when the row did not go as planned.
 *
 * Not a button: a second button beside the first reads as a second answer of
 * equal standing, which is exactly the confusion this is fixing. Underlined text
 * reads as "there is another way", and stays a real control for the keyboard.
 */
const ExceptionAction = ({
	onClick,
	disabled,
	children,
}: {
	onClick: () => void;
	disabled?: boolean;
	children: ReactNode;
}) => (
	<button
		type="button"
		onClick={onClick}
		disabled={disabled}
		className="rounded text-xs text-muted-foreground underline underline-offset-2 transition-colors hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-1 disabled:pointer-events-none disabled:opacity-50"
	>
		{children}
	</button>
);

/**
 * Every row still waiting on an admin, and nothing else.
 *
 * The change-package form creates exactly these rows and then ends; dropping the
 * admin back on the list with a flash message left the work invisible, and sending
 * them to the whole order-details panel made them hunt for it. This is the panel
 * cut down to the part that is actually owed.
 */
export function SettlePaymentsPanel({
	order,
	onClose,
}: {
	order: { id: string; payments: OrderPayment[] };
	onClose: () => void;
}) {
	const { t } = useTranslation("appointments", { useSuspense: false });
	const open = order.payments.filter((payment) => payment.settleable);

	const reload = () =>
		router.reload({ only: ["selectedOrder", "orders", "flash", "errors"] });

	if (open.length === 0) {
		return (
			<div className="space-y-4">
				<p className="text-sm text-muted-foreground text-pretty">
					{t("payments.settle.empty")}
				</p>
				<div className="flex justify-end">
					<Button type="button" size="sm" onClick={onClose}>
						{t("payments.settle.done")}
					</Button>
				</div>
			</div>
		);
	}

	return (
		<div className="space-y-4">
			{/* No preamble: the dialog's own description already said why this is open,
			    and each row states what it is waiting for. */}
			<ul className="space-y-3">
				{open.map((payment) => (
					<li
						key={payment.id}
						className="rounded-lg border border-l-2 border-l-amber-500/70 px-3 py-2.5"
					>
						<div className="flex items-baseline justify-between gap-2">
							<span className="text-sm font-medium">
								{t(`payments.types.${payment.paymentType}`, {
									defaultValue: payment.paymentType.replace(/_/g, " "),
								})}
							</span>
							<span className="text-sm font-semibold tabular-nums shrink-0">
								{isRefund(payment) ? "−" : ""}
								{formatCurrency(payment.amount)}
							</span>
						</div>

						{/* What this row is waiting for, in its own words. The buttons say
						    what an admin can do; this says why they are here at all, and it
						    differs per row — a charge with no link is a different job from
						    one already sent out. */}
						<p className="text-xs text-muted-foreground mt-0.5">
							{t(`payments.settle.state.${waitingOn(payment)}`)}
						</p>

						{payment.description && (
							<p className="text-xs text-muted-foreground mt-1 break-words">
								{payment.description}
							</p>
						)}

						<SettlePaymentActions
							orderId={order.id}
							payment={payment}
							onDone={reload}
							showInvoice
						/>
					</li>
				))}
			</ul>

			{/* Leaving now is allowed — a refund that has not been transferred yet cannot
			    be closed honestly, and the tag on the orders table will still say so. */}
			<div className="flex justify-end">
				<Button type="button" size="sm" variant="ghost" onClick={onClose}>
					{t("payments.settle.later")}
				</Button>
			</div>
		</div>
	);
}

function MarkPaidForm({
	orderId,
	paymentId,
	onDone,
	onCancel,
}: {
	orderId: string;
	paymentId: string;
	onDone: () => void;
	onCancel: () => void;
}) {
	const { t } = useTranslation("appointments", { useSuspense: false });
	const [account, setAccount] = useState<string>(
		COMPANY_BANK_ACCOUNTS[0].accountNumber,
	);
	const [bankCode, setBankCode] = useState("");
	const [destination, setDestination] = useState("");
	// Both halves of the prefill, same as the manual payment-status form: this
	// records the same event on a different row, so it asks for the same two
	// things, in the same words, filled in the same way.
	const [copy, setCopy] = useState(
		prefillFromAccount(COMPANY_BANK_ACCOUNTS[0]),
	);
	const [isLoading, setIsLoading] = useState(false);
	const isOther = account === OTHER_BANK_ACCOUNT;

	const submit = () => {
		setIsLoading(true);
		router.put(
			`/admin-portal/appointments/orders/${orderId}/payments/${paymentId}/mark-paid`,
			{
				form_data: {
					payment_method: PAYMENT_METHOD,
					// Blank description is dropped by the service, which is what keeps the
					// charge's original line ("Biaya tambahan upgrade paket — WI-000185")
					// when nobody types over it. Sending one replaces that line.
					description: copy.description,
					notes: copy.notes,
					...(isOther
						? { bank_code: bankCode, payment_destination: destination }
						: destinationFromAccount(account)),
				},
			},
			{
				preserveScroll: true,
				preserveState: true,
				onFinish: () => setIsLoading(false),
				onSuccess: onDone,
			},
		);
	};

	return (
		<div className="border-t mt-3 pt-3 space-y-3">
			<Label className="text-xs uppercase tracking-wider text-muted-foreground">
				{t("payments.mark_paid.destination_label")}
			</Label>

			<RadioGroup
				value={account}
				onValueChange={(value) => {
					setAccount(value);
					setCopy(
						prefillFromAccount(
							COMPANY_BANK_ACCOUNTS.find(
								({ accountNumber }) => accountNumber === value,
							),
						),
					);
				}}
				className="flex flex-col gap-2"
			>
				{COMPANY_BANK_ACCOUNTS.map(({ bankCode: code, accountNumber }) => (
					<div key={accountNumber} className="flex items-center gap-2">
						<RadioGroupItem
							value={accountNumber}
							id={`mark-paid-${paymentId}-${accountNumber}`}
						/>
						<Label
							htmlFor={`mark-paid-${paymentId}-${accountNumber}`}
							className="font-normal cursor-pointer"
						>
							{`${code} ${accountNumber}`}
						</Label>
					</div>
				))}
				<div className="flex items-center gap-2">
					<RadioGroupItem
						value={OTHER_BANK_ACCOUNT}
						id={`mark-paid-${paymentId}-other`}
					/>
					<Label
						htmlFor={`mark-paid-${paymentId}-other`}
						className="font-normal cursor-pointer"
					>
						{t("payments.mark_paid.other_account")}
					</Label>
				</div>
			</RadioGroup>

			{isOther && (
				<div className="grid grid-cols-2 gap-2">
					<Input
						value={bankCode}
						onChange={(event) => setBankCode(event.target.value)}
						placeholder={t("payments.mark_paid.bank_code_placeholder")}
						className="shadow-inner bg-sidebar"
					/>
					<Input
						value={destination}
						onChange={(event) => setDestination(event.target.value)}
						placeholder={t("payments.mark_paid.destination_placeholder")}
						className="shadow-inner bg-sidebar"
					/>
				</div>
			)}

			{/* The same two fields the manual payment-status form asks for, borrowing
			    its copy verbatim rather than paraphrasing it — one vocabulary for one
			    event, whichever row it lands on. */}
			{(["description", "notes"] as const).map((field) => (
				<div key={field} className="space-y-2">
					<Label
						htmlFor={`mark-paid-${field}-${paymentId}`}
						className="text-xs uppercase tracking-wider text-muted-foreground"
					>
						{t(`form.update_payment.${field}.label`)}
					</Label>
					<Textarea
						id={`mark-paid-${field}-${paymentId}`}
						value={copy[field]}
						onChange={(event) =>
							setCopy((current) => ({
								...current,
								[field]: event.target.value,
							}))
						}
						placeholder={t(`form.update_payment.${field}.placeholder`)}
						className="shadow-inner bg-sidebar"
					/>
				</div>
			))}

			<FormActions
				// An unlisted account has to be written down. Blank values are refused
				// by the controller, and the charge would otherwise settle with no
				// record of where the money actually landed.
				disabled={
					isLoading || (isOther && !(bankCode.trim() && destination.trim()))
				}
				onCancel={onCancel}
				onSubmit={submit}
				submitText={t("payments.actions.mark_paid")}
			/>
		</div>
	);
}

/**
 * Issues a fresh link for one row, with the expiry the admin picks. The new link lands
 * on the row the reload brings back, where it can be copied.
 */
function GenerateLinkForm({
	orderId,
	payment,
	onDone,
	onCancel,
}: {
	orderId: string;
	payment: OrderPayment;
	onDone: () => void;
	onCancel: () => void;
}) {
	const { t } = useTranslation("appointments", { useSuspense: false });
	const [expiry, setExpiry] = useState("180");
	const [isLoading, setIsLoading] = useState(false);

	const submit = () => {
		setIsLoading(true);
		router.post(
			`/admin-portal/appointments/orders/${orderId}/payments/${payment.id}/generate-invoice`,
			{
				form_data: {
					expiry_minutes: Number(expiry),
					// Without it the backend hands a PENDING row's existing link back as is.
					force: payment.status === "PENDING",
				},
			},
			{
				preserveScroll: true,
				preserveState: true,
				onFinish: () => setIsLoading(false),
				onSuccess: onDone,
			},
		);
	};

	return (
		<div className="border-t mt-3 pt-3 space-y-3">
			<Label className="text-xs uppercase tracking-wider text-muted-foreground">
				{t("form.regenerate_invoice.label")}
			</Label>
			<RadioGroup
				value={expiry}
				onValueChange={setExpiry}
				className="flex flex-wrap gap-x-4 gap-y-2"
			>
				{PAYMENT_EXPIRY_OPTIONS.map(({ value, key }) => (
					<div key={value} className="flex items-center gap-2">
						<RadioGroupItem
							value={value}
							id={`new-link-${payment.id}-${value}`}
						/>
						<Label
							htmlFor={`new-link-${payment.id}-${value}`}
							className="font-normal cursor-pointer"
						>
							{t(`form.regenerate_invoice.options.${key}`)}
						</Label>
					</div>
				))}
			</RadioGroup>
			<FormActions
				disabled={isLoading}
				onCancel={onCancel}
				onSubmit={submit}
				submitText={t("payments.actions.new_link")}
			/>
		</div>
	);
}

function MarkRefundedForm({
	orderId,
	paymentId,
	onDone,
	onCancel,
}: {
	orderId: string;
	paymentId: string;
	onDone: () => void;
	onCancel: () => void;
}) {
	const { t } = useTranslation("appointments", { useSuspense: false });
	const [notes, setNotes] = useState("");
	const [isLoading, setIsLoading] = useState(false);

	const submit = () => {
		setIsLoading(true);
		router.put(
			`/admin-portal/appointments/orders/${orderId}/payments/${paymentId}/mark-refunded`,
			{ form_data: { notes } },
			{
				preserveScroll: true,
				preserveState: true,
				onFinish: () => setIsLoading(false),
				onSuccess: onDone,
			},
		);
	};

	return (
		<div className="border-t mt-3 pt-3 space-y-3">
			<Label
				htmlFor={`mark-refunded-notes-${paymentId}`}
				className="text-xs uppercase tracking-wider text-muted-foreground"
			>
				{t("payments.mark_refunded.notes_label")}
			</Label>
			<Textarea
				id={`mark-refunded-notes-${paymentId}`}
				value={notes}
				onChange={(event) => setNotes(event.target.value)}
				placeholder={t("payments.mark_refunded.notes_placeholder")}
				className="shadow-inner bg-sidebar"
			/>
			<p className="text-xs text-muted-foreground">
				{t("payments.mark_refunded.hint")}
			</p>

			<FormActions
				// The transfer reference is the only proof the money actually moved, so
				// there is no way to settle a refund without writing one.
				disabled={isLoading || !notes.trim()}
				onCancel={onCancel}
				onSubmit={submit}
				submitText={t("payments.actions.mark_refunded")}
			/>
		</div>
	);
}

// `disabled` carries both reasons a submit is unavailable — a request in flight and
// a form that is not filled in yet — because the button reads the same either way.
function FormActions({
	disabled,
	onCancel,
	onSubmit,
	submitText,
}: {
	disabled: boolean;
	onCancel: () => void;
	onSubmit: () => void;
	submitText: string;
}) {
	const { t } = useTranslation("appointments", { useSuspense: false });

	return (
		<div className="flex justify-end gap-2">
			<Button type="button" size="sm" variant="ghost" onClick={onCancel}>
				{t("payments.actions.cancel")}
			</Button>
			<Button type="button" size="sm" disabled={disabled} onClick={onSubmit}>
				{submitText}
			</Button>
		</div>
	);
}
